<template>
    <div>
        <div class="calendar-row-vehicle">
            <div class="calendar-header-wrapper">
                <div
                    class="row col-sm-12 nopads"
                >
                    <!-- Calendar controls -->
                    <div class="col-md-3 pb-1 pt-1">
                        <span class="span-title mt-2">{{ $t("daily_summary.date") }}</span>
                        <div class="d-flex align-items-center">
                            <button
                                id="fa-arrow-date-left"
                                class="btn btn-sm btn-outline-secondary"
                                @click="addDays(+7)"
                            >
                                <i class="fa fa-arrow-left"/>
                            </button>
                            <input
                                v-model="calendarStartDate"
                                class="form-control form-control-sm"
                                type="date"
                            >
                            <button
                                id="fa-arrow-date-right"
                                class="btn btn-sm btn-outline-secondary"
                                @click.stop="addDays(-7)"
                            >
                                <i class="fa fa-arrow-right"/>
                            </button>
                        </div>
                    </div>
                    <div class="col-md-6 pb-1 pt-1 mt-1">
                        <open-contract-filter-multi-select
                            :from="calendarStartDate"
                            :show-only-my-contracts-option="true"
                            :show-select-all-contracts-option="true"
                            :use-debounce="true"
                            :contract-collection="null"
                            @loading="loading = true"
                            @loading-completed="loading = false"
                            @company-selected="setCustomer"
                            @contracts-selected="setContracts"
                        />
                    </div>
                    <div
                        id="orderFilter"
                        class="col-md-3"
                    >
                        <span class="span-title" style="margin-top: .85em">{{ $t('calendar.order_status') }} </span>
                        <multiselect
                            v-model="orderStates"
                            class="col-12"
                            :multiple="true"
                            :options="orderStatusValues"
                            :custom-label="value => orderStatusNames[value]"
                            :searchable="true"
                            :close-on-select="false"
                            :placeholder="$t('orders.select_status')"
                            :select-label="$t('orders.select_state')"
                            :selected-label="$t('common.selected')"
                            :deselect-label="$t('common.remove_selection')"
                        />
                    </div>
                </div>

                <div
                    class="calendar-row mb-2"
                >
                    <calendar-header
                        :header-title="$t('trip_list.vehicle')"
                        :text-col-width-percentage="40"
                        :time-col-width="timeColWidth"
                        :visible-weeks-limit="visibleWeeksLimit"
                        :calendar-start-week="calendarStartWeek"
                        :sticky-weeks="true"
                    />

                    <!-- vehicles -->
                    <vehicle-calendar-item
                        v-for="vehicle in vehicles"
                        :key="vehicle.id"
                        ref="vehicles"
                        :vehicle="vehicle"
                        :calendar-start-week="calendarStartWeek"
                        :calendar-start-date="new Date(calendarStartDate)"
                        :visible-weeks-limit="visibleWeeksLimit"
                        :time-col-width="timeColWidth"
                        :text-col-width-percentage="40"
                        :customer-filter="customerFilterSelected"
                        :contract-filter="contractFilterSelected"
                        :auto-expand="autoExpand"
                        :disable-vehicle-selection="disableVehicleActions"
                        :order-state-filter="orderStates"
                        @show-vehicle="showVehicle"
                        @show-order="showOrder"
                        @add-order="addOrderToVehicle"
                        @order-updated="$emit('orderUpdated')"
                    />
                </div>
            </div>
        </div>
        <!-- Vehicle editor -->
        <base-modal
            v-if="selectedVehicle"
            hide-header
            hide-footer
            size="xl"
            @close="selectedVehicle = null"
        >
            <vehicle-editor
                :vehicle="selectedVehicle"
                :contract-trip-list="true"
                :close-when-update="true"
                :user="user"
                :show-title="true"
                :show-close="true"
                @close="selectedVehicle = null"
                @added="closeAndUpdate(selectedVehicle)"
            />
        </base-modal>
        <!-- Add order to vehicle view -->
        <base-modal v-if="vehicleForOrder && !showSelectedOrder" hide-header hide-footer size="lg">
            <order-vehicle-adder
                :from="calendarStartDate"
                :vehicle="vehicleForOrder"
                @cancel="vehicleForOrder = null"
                @update="closeAndUpdate"
            />
        </base-modal>
        <!-- Order editor view -->
        <transition name="fade">
            <div
                v-if="selectedOrder"
                class="details-container"
                @click.stop="selectedOrder = null"
            >
                <div
                    class="details dynamic_width"
                    @click.stop
                >
                    <order-editor
                        v-if="selectedOrder"
                        :order="selectedOrder"
                        :user="user"
                        :show-title="false"
                        @close="selectedOrder = null"
                        @close-and-update="closeOrder"
                    />
                </div>
            </div>
        </transition>
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {workManagementHelper} from "@/components/mixins/WorkManagementMixin";
import {restApi} from "@/components/mixins/RestApiMixin";
import CalendarHeader from "@/components/contractcalendar/CalendarHeader";
import {timeUtils} from "@/components/mixins/TimeUtils";
import OrderEditor from "@/components/order/OrderEditor";
import {userHelper} from "@/components/mixins/UserMixin";
import VehicleCalendarItem from "@/components/vehicle/calendar/VehicleCalendarItem.vue";
import VehicleEditor from "@/components/vehicle/VehicleEditor.vue";
import OpenContractFilterMultiSelect from "@/components/contract/OpenContractFilterMultiSelect.vue";
import OrderVehicleAdder from "@/components/vehicle/calendar/OrderVehicleAdder";
import {inject} from "vue";
import BaseModal from "@/components/BaseModal";
import {orderStatus} from "../../mixins/WorkManagementMixin.js";

export default {
    name: "VehicleCalendar",
    components: {
        BaseModal,
        OrderVehicleAdder,
        OpenContractFilterMultiSelect,
        VehicleEditor,
        VehicleCalendarItem,
        OrderEditor,
        CalendarHeader,
    },
    mixins: [workManagementHelper, restApi, timeUtils, userHelper],
    props: {
        user: {
            type: Object,
            default: null,
        },
        disableVehicleActions: {
            type: Boolean,
            default: false,
        },
        autoExpand: {
            type: Boolean,
            default: false,
        },
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject("showAlert");
        return {showAlert};
    },
    data: function () {
        return {
            visibleWeeksLimit: 23,
            calendarStartDate: this.getCurrentDay(),
            calendarStartWeek: this.getWeekNumber(this.getCurrentDay()),
            timeColWidth: 50,
            vehicles: [],
            selectedVehicle: null,
            customerFilterSelected: [],
            contractFilterSelected: [],
            vehicleForOrder: null,
            showSelectedOrder: false,
            selectedOrder: null,
            onlyUserContracts: 1,
            loading: false,
            orderStates: [],
        };
    },
    computed: {
        orderStatusNames() {
            return {
                [orderStatus.STORED]: this.$t('orders.status_stored'),
                [orderStatus.PLAN]: this.$t('orders.status_plan'),
                [orderStatus.OPEN]: this.$t('orders.status_open'),
                [orderStatus.IN_PROGRESS]: this.$t('orders.status_in_progress'),
                [orderStatus.COMPLETED]: this.$t('orders.status_completed'),
                [orderStatus.CANCELLED]: this.$t('orders.status_cancelled'),
                [orderStatus.ABORTED]: this.$t('orders.status_aborted'),
                [orderStatus.APPROVED]: this.$t('orders.status_approved'),
                [orderStatus.INVOICED]: this.$t('orders.status_invoiced'),
                [orderStatus.CLOSED]: this.$t('orders.status_closed'),
            }
        },
        orderStatusValues() {
            return Object.values(orderStatus)
        }
    },
    watch: {
        calendarStartDate() {
            this.calendarStartWeek = this.getWeekNumber(this.calendarStartDate);
        },
        onlyUserContracts() {
            this.lastSearchedParams.onlyUserOwnedContracts = this.onlyUserContracts;
        },
    },
    mounted() {
        let now = this.calendarStartDate;
        let to = new Date();
        to.setDate(to.getDate() + 7 * this.visibleWeeksLimit);
        this.lastSearchedParams = {
            startDate: now,
            endDate: to.toISOString(),
            in_use: 1,
        };
        this.fetchVehicles(this.lastSearchedParams);
        this.orderStates=[orderStatus.OPEN, orderStatus.IN_PROGRESS, orderStatus.PLAN]
    },
    methods: {
        fetchVehicles: function (params) {
            this.loading = true;
            this.lastSearchedParams = params;
            this.vehicles = [];
            this.restFetchParams(this.vehicleUrl, this.lastSearchedParams, this.handleVehicles);
        },

        handleVehicles: function (response) {
            this.loading = false;
            if (response && response.data) {
                this.vehicles = response.data;
            }
        },
        closeAndUpdate(updatedVehicle) {
            let index = this.vehicles.findIndex((item) => item.id === updatedVehicle.id);
            if (index >= 0) {
                this.$refs.vehicles[index].refreshVehicle(updatedVehicle);
            }
            this.selectedVehicle = null;
            this.vehicleForOrder = null;
        },
        addDays(days) {
            let now = new Date(this.calendarStartDate);
            now.setDate(now.getDate() - days);
            now.setHours(12);
            this.calendarStartDate = now.toISOString().slice(0, 10);
        },
        showVehicle(vehicle) {
            if (!this.disableVehicleActions) {
                if (vehicle && vehicle.id) {
                    this.fetchVehicle(vehicle.id);
                }
            }
        },
        fetchVehicle: function (id) {
            this.loading = true;
            let params = {};
            params.id = id;
            this.restFetchParams(this.vehicleUrl, params, this.handleVehicle, this.fail);
        },
        handleVehicle: function (response) {
            let fetchedVehicle = response ? response.data[0] : {};
            this.selectedVehicle = this.jsonToItem(fetchedVehicle);
            this.loading = false;
        },
        addOrderToVehicle: function (vehicle) {
            this.vehicleForOrder = vehicle;
        },
        showOrder(order, vehicle) {
            if (order && order.id) {
                this.showSelectedOrder = true;
                this.vehicleForOrder = vehicle;
                this.fetchOrder(order.id);
            }
        },
        fetchOrder: function (id) {
            this.loading = true;
            this.restFetch(this.orderUrl + "/" + id, this.handleOrders, this.fail);
        },
        handleOrders: function (response) {
            let fetchedOrder = response ? response.data : {};
            this.selectedOrder = this.createEditorOrder(fetchedOrder);
            this.loading = false;
        },
        setCustomer(customer) {
            this.customerFilterSelected = customer ? [customer] : [];
        },
        setContracts(contracts) {
            this.contractFilterSelected = contracts;
        },
        closeOrder() {
            if (this.selectedOrder !== null) {
                this.refresh();
                this.$emit("orderUpdated");
                this.selectedOrder = null;
                this.showSelectedOrder = false;
                this.vehicleForOrder = null;
            }
        },
        fail: function () {
            this.showAlert(this.$t("common.alert_update_failed"));
            this.loading = false;
        },
        refresh() {
            let index = this.vehicles.findIndex((item) => item.id === this.vehicleForOrder.id);
            if (index >= 0) {
                this.$refs.vehicles[index].refreshVehicle(this.vehicles[index]);
            }
        },
    },
};
</script>
<style scoped>
#fa-arrow-date-right,
#fa-arrow-date-left {
    background: #ffffff;
    border-top: 1px #bcbcbc solid;
    border-right: 1px #bcbcbc solid;
    color: #3b3933;
    margin: 0.1em;
    padding: 0.3em;
    font-size: 0.8em;
    user-select: none;
    min-height: 0.3em;
}

#fa-arrow-date-right:hover,
#fa-arrow-date-left:hover {
    font-weight: bold;
}

@media (min-width: 768px) {
    #filter {
        top: -20px;
    }
}

.calendar-header-wrapper {
    position: sticky;
    top: 0;
    z-index: 20;
    background: white;
}

.calendar-row-vehicle {
    display: block;
    width: auto;
    padding-bottom: 0.5em;
}
</style>
